import { useMutation } from '@tanstack/react-query';
import { log, logUnknownErrorResponse } from '@hungryroot/o11y';
import { StatusCode } from '@hungryroot/rest';
import {
  postApiV2AuthSignup as signupApiCall,
  type SignupParams as ApiSignupParams,
  type SignupResponse,
} from '@hungryroot/rest';

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

interface SignupError {
  detail: string | null;
  msg_id: string | null;
}

interface SignupParams {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  captcha_token?: string;
  /**
   * Marks a freemium (guest-flow) signup; the backend creates the customer
   * with `CustomerStatus.GUEST`. Only sent to the API when true.
   */
  isGuestFlow?: boolean;
}

type InvalidPasswordErrors = 'too-short' | 'too-common';

function getSignupError(resp: {
  status: number;
  data: { detail: string | null; msg_id: string | null };
}) {
  const { data, status: respStatus } = resp;

  if (typeof data.detail !== 'string') {
    return {
      type: 'unknown',
    } as const;
  }

  if (respStatus === 400) {
    switch (true) {
      case /Invalid password/.test(data.detail):
      case /password is too short/.test(data.detail):
      case /password is too common/.test(data.detail): {
        const errors: InvalidPasswordErrors[] = [];
        if (
          /Invalid password/.test(data.detail) ||
          /password is too short/.test(data.detail)
        ) {
          errors.push('too-short');
        }

        if (/password is too common/.test(data.detail)) {
          errors.push('too-common');
        }

        return {
          type: 'invalid-password',
          errors,
        } as const;
      }

      case /Enter a valid email address\./.test(data.detail): {
        return {
          type: 'invalid-email',
        } as const;
      }

      default: {
        logUnknownErrorResponse('Unexpected 400 on signup', {
          key: 'useSignup.badRequest',
          status: respStatus,
          detail: data.detail,
        });

        return {
          type: 'bad-request',
        } as const;
      }
    }
  }

  if (respStatus === 409) {
    return {
      type: 'user-already-exists',
    } as const;
  }

  logUnknownErrorResponse('Unexpected error on signup', {
    key: 'useSignup.unknown',
    status: respStatus,
    detail: data.detail,
  });

  return {
    type: 'unknown',
  } as const;
}

type SignupErrorResponse = ReturnType<typeof getSignupError>;

export function useSignup() {
  return useMutation<SignupResponse, SignupErrorResponse, SignupParams>({
    mutationFn: async (params) => {
      ga('send', 'event', 'signup', 'submit');
      log('signup', {
        lifecycle: 'initiated',
      });

      // Map the frontend params to the API params format
      const apiParams: ApiSignupParams = {
        email: params.email,
        password: params.password,
        ...(params.firstName !== undefined
          ? { first_name: params.firstName }
          : {}),
        ...(params.lastName !== undefined
          ? { last_name: params.lastName }
          : {}),
        ...(params.captcha_token && { captcha_token: params.captcha_token }),
        ...(params.isGuestFlow === true ? { is_guest_flow: true } : {}),
      };

      const resp = await signupApiCall(apiParams);

      const { status: respStatus } = resp;
      if (
        respStatus === StatusCode.SuccessCreated &&
        !('detail' in resp.data)
      ) {
        const { data } = resp;
        return data;
      }

      throw getSignupError(
        resp as unknown as {
          status: number;
          data: SignupError;
        }
      );
    },
    onSuccess: (data) => {
      log('signup', {
        lifecycle: 'success',
      });
      ga('send', 'event', 'signup', 'success');
      window.dataLayer?.push({
        event: 'hr.signup.success',
        signup: {
          method: 'email',
        },
        customer: {
          id: data.id,
          email: data.email,
        },
      });
    },
  });
}
